import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { useNotification } from '../components/NotificationContext';
import { DpacClient, AlgorithmLabel, PeerServer } from '../types';

const AdminConfig: React.FC = () => {
    const [clients, setClients] = useState<DpacClient[]>([]);
    const { notify } = useNotification();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Form state for new/editing client
    const [editingClient, setEditingClient] = useState<Partial<DpacClient> | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    
    // Algorithm Label state
    const [algorithms, setAlgorithms] = useState<AlgorithmLabel[]>([]);
    const [editingAlgorithm, setEditingAlgorithm] = useState<Partial<AlgorithmLabel> | null>(null);
    const [isAddingAlgorithm, setIsAddingAlgorithm] = useState(false);
    const [savingAlgorithm, setSavingAlgorithm] = useState(false);

    // Peer Server state
    const [peerServers, setPeerServers] = useState<PeerServer[]>([]);
    const [editingPeer, setEditingPeer] = useState<Partial<PeerServer> | null>(null);
    const [isAddingPeer, setIsAddingPeer] = useState(false);
    const [savingPeer, setSavingPeer] = useState(false);

    useEffect(() => {
        fetchClients();
        fetchAlgorithms();
        fetchPeerServers();
    }, []);

    const fetchClients = async () => {
        try {
            setLoading(true);
            const data = await api.get('/admin/dpac-clients');
            setClients(data.clients || []);
        } catch (error: any) {
            notify(error.message || 'Errore nel recupero dei client DPAC.', 'error');
        } finally {
            if (algorithms.length > 0 && peerServers.length > 0) setLoading(false);
        }
    };

    const fetchAlgorithms = async () => {
        try {
            setLoading(true);
            const data = await api.get('/admin/algorithm-labels');
            setAlgorithms(data.algorithm_labels || []);
        } catch (error: any) {
            notify(error.message || 'Errore nel recupero degli algoritmi label.', 'error');
        } finally {
            if (peerServers.length > 0) setLoading(false);
        }
    };

    const fetchPeerServers = async () => {
        try {
            setLoading(true);
            const data = await api.get('/admin/peer-servers');
            setPeerServers(data.peer_servers || []);
        } catch (error: any) {
            notify(error.message || 'Errore nel recupero dei server cooperativi.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSavePeer = async () => {
        if (!editingPeer?.name || !editingPeer?.url) {
            notify('Nome e URL sono obbligatori.', 'error');
            return;
        }

        try {
            setSavingPeer(true);
            if (editingPeer.id) {
                await api.put(`/admin/peer-servers/${editingPeer.id}`, editingPeer);
                notify('Server cooperativo aggiornato con successo!', 'success');
            } else {
                await api.post('/admin/peer-servers', editingPeer);
                notify('Server cooperativo creato con successo!', 'success');
            }
            setEditingPeer(null);
            setIsAddingPeer(false);
            fetchPeerServers();
        } catch (error: any) {
            notify(error.message || 'Errore durante il salvataggio del server cooperativo.', 'error');
        } finally {
            setSavingPeer(false);
        }
    };

    const handleDeletePeer = async (id: number) => {
        if (!window.confirm('Sei sicuro di voler eliminare questo server cooperativo?')) return;
        
        try {
            await api.delete(`/admin/peer-servers/${id}`);
            notify('Server cooperativo eliminato con successo!', 'success');
            fetchPeerServers();
        } catch (error: any) {
            notify(error.message || 'Impossibile eliminare il server cooperativo.', 'error');
        }
    };

    const handleSaveAlgorithm = async () => {
        if (!editingAlgorithm?.name || !editingAlgorithm?.script) {
            notify('Nome e Script sono obbligatori.', 'error');
            return;
        }

        try {
            setSavingAlgorithm(true);
            if (editingAlgorithm.id) {
                await api.put(`/admin/algorithm-labels/${editingAlgorithm.id}`, editingAlgorithm);
                notify('Algoritmo Label aggiornato con successo!', 'success');
            } else {
                await api.post('/admin/algorithm-labels', editingAlgorithm);
                notify('Algoritmo Label creato con successo!', 'success');
            }
            setEditingAlgorithm(null);
            setIsAddingAlgorithm(false);
            fetchAlgorithms();
        } catch (error: any) {
            notify(error.message || 'Errore durante il salvataggio dell\'algoritmo.', 'error');
        } finally {
            setSavingAlgorithm(false);
        }
    };

    const handleDeleteAlgorithm = async (id: number) => {
        if (!window.confirm('Sei sicuro di voler eliminare questo algoritmo?')) return;
        
        try {
            await api.delete(`/admin/algorithm-labels/${id}`);
            notify('Algoritmo Label eliminato con successo!', 'success');
            fetchAlgorithms();
        } catch (error: any) {
            notify(error.message || 'Impossibile eliminare l\'algoritmo. Potrebbe essere associato a dei cantieri.', 'error');
        }
    };

    const handleSaveClient = async () => {
        if (!editingClient?.name || !editingClient?.transfer_archive || !editingClient?.axway_monitoring_path) {
            notify('Tutti i campi sono obbligatori.', 'error');
            return;
        }

        try {
            setSaving(true);
            if (editingClient.id) {
                await api.put(`/admin/dpac-clients/${editingClient.id}`, editingClient);
                notify('Client DPAC aggiornato con successo!', 'success');
            } else {
                await api.post('/admin/dpac-clients', editingClient);
                notify('Client DPAC creato con successo!', 'success');
            }
            setEditingClient(null);
            setIsAdding(false);
            fetchClients();
        } catch (error: any) {
            notify(error.message || 'Errore durante il salvataggio del client.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteClient = async (id: number) => {
        if (!window.confirm('Sei sicuro di voler eliminare questo client?')) return;
        
        try {
            await api.delete(`/admin/dpac-clients/${id}`);
            notify('Client DPAC eliminato con successo!', 'success');
            fetchClients();
        } catch (error: any) {
            notify(error.message || 'Impossibile eliminare il client. Potrebbe essere associato a dei lotti.', 'error');
        }
    };

    if (loading) {
        return (
            <div className="container" style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
                <div className="spinner-small" style={{ width: '40px', height: '40px' }} />
            </div>
        );
    }

    return (
        <div className="container">
            <div className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h2 style={{ color: 'var(--primary-color)', margin: 0 }}>Gestione Client DPAC</h2>
                    {!isAdding && !editingClient && (
                        <button className="btn btn-primary" onClick={() => { setIsAdding(true); setEditingClient({}); }}>
                            <i className="fas fa-plus" style={{ marginRight: '8px' }} />
                            AGGIUNGI CLIENT
                        </button>
                    )}
                </div>

                <p style={{ marginBottom: '32px', color: 'rgba(0,0,0,0.6)' }}>
                    Configura gli account Axway per il trasferimento dei pacchetti verso il sistema DPAC.
                </p>

                {(isAdding || editingClient) && (
                    <div style={{ 
                        background: '#f9f9f9', 
                        padding: '24px', 
                        borderRadius: '12px', 
                        marginBottom: '32px',
                        border: '1px solid #eee' 
                    }}>
                        <h3 style={{ marginBottom: '20px', fontSize: '1.1rem' }}>
                            {editingClient?.id ? 'Modifica Client' : 'Nuovo Client DPAC'}
                        </h3>
                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label>Nome Client</label>
                            <input 
                                type="text" 
                                value={editingClient?.name || ''} 
                                onChange={e => setEditingClient(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Esempio: Default Client DPAC"
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label>Transfer Archive Path</label>
                            <input 
                                type="text" 
                                value={editingClient?.transfer_archive || ''} 
                                onChange={e => setEditingClient(prev => ({ ...prev, transfer_archive: e.target.value }))}
                                placeholder="/path/to/archive"
                                style={{ fontFamily: 'monospace' }}
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: '24px' }}>
                            <label>Axway Monitoring Path</label>
                            <input 
                                type="text" 
                                value={editingClient?.axway_monitoring_path || ''} 
                                onChange={e => setEditingClient(prev => ({ ...prev, axway_monitoring_path: e.target.value }))}
                                placeholder="/path/to/axway/monitoring"
                                style={{ fontFamily: 'monospace' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button className="btn" onClick={() => { setIsAdding(false); setEditingClient(null); }}>
                                ANNULLA
                            </button>
                            <button className="btn btn-primary" onClick={handleSaveClient} disabled={saving}>
                                {saving ? <div className="spinner-small" /> : 'SALVA'}
                            </button>
                        </div>
                    </div>
                )}

                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Archive Path</th>
                                <th>Axway Path</th>
                                <th style={{ textAlign: 'right' }}>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clients.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                        Nessun client configurato.
                                    </td>
                                </tr>
                            ) : (
                                clients.map(client => (
                                    <tr key={client.id}>
                                        <td style={{ fontWeight: 'bold' }}>{client.name}</td>
                                        <td style={{ fontSize: '0.85rem', color: '#666' }}><code>{client.transfer_archive}</code></td>
                                        <td style={{ fontSize: '0.85rem', color: '#666' }}><code>{client.axway_monitoring_path}</code></td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button 
                                                className="btn-icon" 
                                                onClick={() => setEditingClient(client)}
                                                title="Modifica"
                                                style={{ color: 'var(--primary-color)' }}
                                            >
                                                <i className="fas fa-edit" />
                                            </button>
                                            <button 
                                                className="btn-icon" 
                                                onClick={() => handleDeleteClient(client.id)}
                                                title="Elimina"
                                                style={{ color: '#e74c3c', marginLeft: '8px' }}
                                                disabled={client.name === 'Default Client DPAC'}
                                            >
                                                <i className="fas fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Algorithm Label Section */}
            <div className="card" style={{ marginTop: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h2 style={{ color: 'var(--primary-color)', margin: 0 }}>Gestione Algoritmi Label</h2>
                    {!isAddingAlgorithm && !editingAlgorithm && (
                        <button className="btn btn-primary" onClick={() => { setIsAddingAlgorithm(true); setEditingAlgorithm({}); }}>
                            <i className="fas fa-plus" style={{ marginRight: '8px' }} />
                            AGGIUNGI ALGORITMO
                        </button>
                    )}
                </div>

                <p style={{ marginBottom: '32px', color: 'rgba(0,0,0,0.6)' }}>
                    Definisci gli algoritmi personalizzati per l'estrazione delle label dai nomi dei file.
                </p>

                {(isAddingAlgorithm || editingAlgorithm) && (
                    <div style={{ 
                        background: '#f9f9f9', 
                        padding: '24px', 
                        borderRadius: '12px', 
                        marginBottom: '32px',
                        border: '1px solid #eee' 
                    }}>
                        <h3 style={{ marginBottom: '20px', fontSize: '1.1rem' }}>
                            {editingAlgorithm?.id ? 'Modifica Algoritmo' : 'Nuovo Algoritmo Label'}
                        </h3>
                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label>Nome Algoritmo</label>
                            <input 
                                type="text" 
                                value={editingAlgorithm?.name || ''} 
                                onChange={e => setEditingAlgorithm(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Esempio: Algoritmo Cantiere X"
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label>Script (.py)</label>
                            <input 
                                type="text" 
                                value={editingAlgorithm?.script || ''} 
                                onChange={e => setEditingAlgorithm(prev => ({ ...prev, script: e.target.value }))}
                                placeholder="percorso/al/file.py"
                                style={{ fontFamily: 'monospace' }}
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: '24px' }}>
                            <label>Descrizione</label>
                            <textarea 
                                value={editingAlgorithm?.description || ''} 
                                onChange={e => setEditingAlgorithm(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Descrizione dell'algoritmo..."
                                rows={3}
                                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button className="btn" onClick={() => { setIsAddingAlgorithm(false); setEditingAlgorithm(null); }}>
                                ANNULLA
                            </button>
                            <button className="btn btn-primary" onClick={handleSaveAlgorithm} disabled={savingAlgorithm}>
                                {savingAlgorithm ? <div className="spinner-small" /> : 'SALVA'}
                            </button>
                        </div>
                    </div>
                )}

                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nome Algoritmo</th>
                                <th>Script (.py)</th>
                                <th>Descrizione</th>
                                <th style={{ textAlign: 'right' }}>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {algorithms.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                        Nessun algoritmo configurato.
                                    </td>
                                </tr>
                            ) : (
                                algorithms.map(algo => (
                                    <tr key={algo.id}>
                                        <td style={{ fontWeight: 'bold' }}>{algo.name}</td>
                                        <td style={{ fontSize: '0.85rem', color: '#666' }}><code>{algo.script}</code></td>
                                        <td style={{ fontSize: '0.85rem', color: '#666' }}>{algo.description || '-'}</td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button 
                                                className="btn-icon" 
                                                onClick={() => setEditingAlgorithm(algo)}
                                                title="Modifica"
                                                style={{ color: 'var(--primary-color)' }}
                                            >
                                                <i className="fas fa-edit" />
                                            </button>
                                            <button 
                                                className="btn-icon" 
                                                onClick={() => handleDeleteAlgorithm(algo.id)}
                                                title="Elimina"
                                                style={{ color: '#e74c3c', marginLeft: '8px' }}
                                                disabled={algo.name === 'Genera Label (Default)'}
                                            >
                                                <i className="fas fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Peer Server Section */}
            <div className="card" style={{ marginTop: '32px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <h2 style={{ color: 'var(--primary-color)', margin: 0 }}>Gestione Server Cooperativi</h2>
                    {!isAddingPeer && !editingPeer && (
                        <button className="btn btn-primary" onClick={() => { setIsAddingPeer(true); setEditingPeer({}); }}>
                            <i className="fas fa-plus" style={{ marginRight: '8px' }} />
                            AGGIUNGI SERVER
                        </button>
                    )}
                </div>

                <p style={{ marginBottom: '32px', color: 'rgba(0,0,0,0.6)' }}>
                    Configura gli altri server installati che partecipano alla creazione del report statistico generale.
                </p>

                {(isAddingPeer || editingPeer) && (
                    <div style={{ 
                        background: '#f9f9f9', 
                        padding: '24px', 
                        borderRadius: '12px', 
                        marginBottom: '32px',
                        border: '1px solid #eee' 
                    }}>
                        <h3 style={{ marginBottom: '20px', fontSize: '1.1rem' }}>
                            {editingPeer?.id ? 'Modifica Server' : 'Nuovo Server Cooperativo'}
                        </h3>
                        <div className="form-group" style={{ marginBottom: '16px' }}>
                            <label>Nome Server</label>
                            <input 
                                type="text" 
                                value={editingPeer?.name || ''} 
                                onChange={e => setEditingPeer(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Esempio: Server Sede Secondaria"
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: '24px' }}>
                            <label>URL API Server</label>
                            <input 
                                type="text" 
                                value={editingPeer?.url || ''} 
                                onChange={e => setEditingPeer(prev => ({ ...prev, url: e.target.value }))}
                                placeholder="http://192.168.1.100:5001"
                                style={{ fontFamily: 'monospace' }}
                            />
                        </div>
                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button className="btn" onClick={() => { setIsAddingPeer(false); setEditingPeer(null); }}>
                                ANNULLA
                            </button>
                            <button className="btn btn-primary" onClick={handleSavePeer} disabled={savingPeer}>
                                {savingPeer ? <div className="spinner-small" /> : 'SALVA'}
                            </button>
                        </div>
                    </div>
                )}

                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Nome Server</th>
                                <th>URL</th>
                                <th style={{ textAlign: 'right' }}>Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                            {peerServers.length === 0 ? (
                                <tr>
                                    <td colSpan={3} style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                                        Nessun server cooperativo configurato.
                                    </td>
                                </tr>
                            ) : (
                                peerServers.map(server => (
                                    <tr key={server.id}>
                                        <td style={{ fontWeight: 'bold' }}>{server.name}</td>
                                        <td style={{ fontSize: '0.85rem', color: '#666' }}><code>{server.url}</code></td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button 
                                                className="btn-icon" 
                                                onClick={() => setEditingPeer(server)}
                                                title="Modifica"
                                                style={{ color: 'var(--primary-color)' }}
                                            >
                                                <i className="fas fa-edit" />
                                            </button>
                                            <button 
                                                className="btn-icon" 
                                                onClick={() => handleDeletePeer(server.id)}
                                                title="Elimina"
                                                style={{ color: '#e74c3c', marginLeft: '8px' }}
                                            >
                                                <i className="fas fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AdminConfig;
